import React from 'react';
import useCarousel from '../hooks/useCarousel';

import horse1 from '../assets/images/horses/Buckwheat.jpg';
import horse2 from '../assets/images/horses/Dixie.jpg';
import horse3 from '../assets/images/horses/Doc.jpg';
import horse4 from '../assets/images/horses/Nathan.jpg';
import horse5 from '../assets/images/horses/Norton.jpg';

const horseImages = [horse1, horse2, horse3, horse4, horse5];

const ChevronLeft = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRight = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
  </svg>
);

const DonationsSection = ({ onDonateClick }) => {
  const { currentIndex, next, prev, goTo, pause, resume } = useCarousel(horseImages.length);

  return (
    <div id="donations" className="container mx-auto px-4 sm:px-6 pt-6 pb-6 sm:py-16 relative z-10">
      <div className="glass-section">
        <h2 className="text-3xl lg:text-4xl font-bold mb-6 sm:mb-8 drop-shadow-xl text-center font-display text-white">
          Donations
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">

          {/* Horse Carousel */}
          <div
            className="relative order-2 lg:order-1 w-full sm:w-3/4 lg:w-full mx-auto"
            onMouseEnter={pause}
            onMouseLeave={resume}
          >
            <div className="relative overflow-hidden rounded-2xl shadow-2xl">
              <div className="relative w-full pt-[133%]">
                {horseImages.map((img, index) => (
                  <img
                    key={index}
                    src={img}
                    alt="One of TRNF's therapy horses"
                    className={`absolute top-0 left-0 w-full h-full object-cover transition-opacity duration-500 ease-in-out ${
                      index === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'
                    }`}
                  />
                ))}
              </div>

              <div className="absolute top-1/2 -translate-y-1/2 left-0 right-0 flex justify-between px-2 sm:px-3 z-20">
                <button
                  onClick={prev}
                  aria-label="Previous horse photo"
                  className="bg-black/50 text-white p-2 sm:p-3 rounded-full hover:bg-black/70 transition-all duration-300 backdrop-blur-sm min-w-[44px] min-h-[44px] flex items-center justify-center"
                >
                  <ChevronLeft />
                </button>
                <button
                  onClick={next}
                  aria-label="Next horse photo"
                  className="bg-black/50 text-white p-2 sm:p-3 rounded-full hover:bg-black/70 transition-all duration-300 backdrop-blur-sm min-w-[44px] min-h-[44px] flex items-center justify-center"
                >
                  <ChevronRight />
                </button>
              </div>

              <span className="sr-only" aria-live="polite">
                Horse photo {currentIndex + 1} of {horseImages.length}
              </span>
            </div>

            <div className="flex justify-center gap-2 mt-3" role="tablist" aria-label="Horse photos">
              {horseImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goTo(index)}
                  role="tab"
                  aria-selected={index === currentIndex}
                  aria-label={`Go to horse photo ${index + 1}`}
                  className={`rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? 'w-4 h-2.5 bg-green-300'
                      : 'w-2.5 h-2.5 bg-white/40 hover:bg-white/70'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Donations Content */}
          <div className="text-white order-1 lg:order-2">
            <blockquote className="mb-6 border-l-4 border-green-300/60 pl-5">
              <p className="font-display italic text-xl sm:text-2xl text-green-300 leading-relaxed drop-shadow-lg mb-2"
                 style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.8)' }}>
                "Nothing does as much for the inside of a man as the outside of a horse."
              </p>
              <footer className="text-white/60 text-sm">— Theodore Roosevelt</footer>
            </blockquote>

            <div className="space-y-4">
              <p className="text-base sm:text-lg lg:text-xl leading-relaxed drop-shadow-lg text-center lg:text-left">
                Therapeutic riding benefits people with special needs in countless ways—both inside and out!
                Horses provide a profound sense of independence and equine activities foster self-awareness,
                build self-confidence, and enhance concentration. They also improve posture, balance,
                coordination, strength, and flexibility!
              </p>
              <p className="text-base sm:text-lg lg:text-xl leading-relaxed drop-shadow-lg text-center lg:text-left">
                Donate today to help continue providing the benefits of therapeutic riding to residents of
                Harford County.
              </p>

              <div className="flex justify-center pt-2">
                <button
                  onClick={onDonateClick}
                  className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 sm:py-4 px-6 sm:px-8 rounded-full text-base sm:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center min-h-[48px]"
                >
                  Donate To Our Cause
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default DonationsSection;
